/**
 * The vault key hierarchy.
 *
 *   password ──Argon2id(kek_salt)──→ KEK ─┐
 *                                          ├──→ masterKey ──→ userKeyPair (ECDH P-256)
 *   24 words ──HKDF(entropy)────────→ RK ─┘         │
 *                                                   └──→ rootNode.nodeKey
 *                                                              │
 *                                        nodeKey wrapped under its PARENT's nodeKey
 *                                                              │
 *                                                   contentKey wrapped under nodeKey
 *                                                              │
 *                                                     file bytes, chunked (stream.ts)
 *
 * Three properties are worth stating outright, because every later decision
 * follows from them:
 *
 *  1. **KEK and RK are peers.** Either unwraps the master key; neither reveals
 *     the other. That is the whole recovery story — losing the password costs
 *     nothing if the phrase survives, and vice versa. Losing both is
 *     unrecoverable, by construction and not by policy.
 *
 *  2. **A node key is wrapped under its parent's key, never under the master
 *     key.** Handing someone a folder key gives them that folder and
 *     everything under it, and nothing above it. Sharing a subtree is
 *     therefore one key handed over, not a re-encryption pass.
 *
 *  3. **contentKey is separate from nodeKey.** Rename, move and re-share all
 *     rewrap the node key and never touch the file body. It is also what makes
 *     version history cheap later: revisions can share a content key.
 */

import {
  aesDecrypt,
  aesEncrypt,
  exportRaw,
  fromBase64,
  generateAesKey,
  hkdfAesKey,
  importAesKey,
  randomBytes,
  sha256,
  toBase64,
  toHex,
  utf8,
  type Bytes
} from './primitives';
import { DEFAULT_KDF_PARAMS, deriveKek, type KdfParams } from './argon2';

/** Salt for the recovery-key derivation. Fixed, and part of the format: change
 *  it and every recovery phrase in existence stops working. */
const RECOVERY_SALT = 'mauvely.cloud.recovery.v1';

export const KEK_SALT_BYTES = 16;

// ── master key ──────────────────────────────────────────────────────────────

/**
 * The recovery key, from the 24 words' entropy.
 *
 * Straight HKDF, no stretching: the input is 256 uniformly random bits, so
 * there is nothing to slow an attacker down about — they would have to search
 * a 2^256 space either way.
 */
export const deriveRecoveryKey = (entropy: Bytes): Promise<CryptoKey> =>
  hkdfAesKey(entropy, 'mauvely.cloud.recovery-key.v1', utf8(RECOVERY_SALT));

/**
 * What unlocking needs.
 *
 * Deliberately excludes the root node's wrapped key: that belongs to the node
 * tree, not to the vault, and arrives with the folder listing like every other
 * node's does. Requiring it here would force the unlock screen to fetch a
 * folder before it could open the keys that read folders.
 */
export interface VaultBlobs {
  kekSalt: string;
  kdfParams: KdfParams;
  masterKeyWrappedPassword: string;
  masterKeyWrappedRecovery: string;
  publicKey: string;
  publicKeyFingerprint: string;
  privateKeyWrapped: string;
}

/** What first-run setup posts: the vault, plus the root folder it creates. */
export interface NewVaultBlobs extends VaultBlobs {
  rootNodeKeyWrapped: string;
}

/** An unlocked vault: the keys held in memory once a password or phrase has
 *  opened it. None of these are extractable except where wrapping demands it. */
export interface UnlockedVault {
  masterKey: CryptoKey;
  privateKey: CryptoKey;
  publicKey: CryptoKey;
  publicKeyFingerprint: string;
}

/**
 * Build a brand-new vault. Runs once per account, at first sign-in to Cloud.
 *
 * Returns the blobs to persist and the phrase to show the user exactly once.
 * The caller must not store the phrase anywhere — displaying it and forgetting
 * it is the entire contract.
 */
export async function createVault(
  password: string,
  recoveryEntropy: Bytes,
  params: KdfParams = DEFAULT_KDF_PARAMS
): Promise<{ blobs: NewVaultBlobs; vault: UnlockedVault; rootNodeKey: CryptoKey }> {
  const kekSalt = randomBytes(KEK_SALT_BYTES);

  // Extractable only so it can be wrapped twice below; it is re-imported
  // non-extractable before anything else touches it.
  const masterRaw = randomBytes(32);

  const kek = await deriveKek(password, kekSalt, params);
  const rk = await deriveRecoveryKey(recoveryEntropy);

  const masterKeyWrappedPassword = await aesEncrypt(kek, masterRaw, WRAP_AAD.master);
  const masterKeyWrappedRecovery = await aesEncrypt(rk, masterRaw, WRAP_AAD.master);

  const masterKey = await importAesKey(masterRaw, false);
  masterRaw.fill(0);

  const pair = await generateUserKeyPair();
  const privateKeyWrapped = await wrapPrivateKey(masterKey, pair.privateKey);
  const { spki, fingerprint } = await exportPublicKey(pair.publicKey);

  // The root folder holds every other key in the tree, so it is the one node
  // whose key hangs directly off the master key.
  const rootNodeKey = await generateAesKey();
  const rootNodeKeyWrapped = await wrapNodeKey(masterKey, rootNodeKey);

  return {
    blobs: {
      kekSalt: toBase64(kekSalt),
      kdfParams: params,
      masterKeyWrappedPassword,
      masterKeyWrappedRecovery,
      publicKey: spki,
      publicKeyFingerprint: fingerprint,
      privateKeyWrapped,
      rootNodeKeyWrapped
    },
    vault: {
      masterKey,
      privateKey: pair.privateKey,
      publicKey: pair.publicKey,
      publicKeyFingerprint: fingerprint
    },
    rootNodeKey
  };
}

/**
 * Additional authenticated data for each kind of wrap.
 *
 * GCM already stops a ciphertext being altered. AAD stops it being *moved*: a
 * wrapped node key spliced into the master-key column fails to decrypt instead
 * of quietly succeeding, because the label it was sealed with no longer
 * matches. Cheap, and it removes a whole class of confused-deputy bug.
 */
const WRAP_AAD = {
  master: utf8('mauvely.cloud.wrap.master'),
  private: utf8('mauvely.cloud.wrap.private'),
  node: utf8('mauvely.cloud.wrap.node'),
  content: utf8('mauvely.cloud.wrap.content')
} as const;

/**
 * The master-key AAD, exported for the passkey wrap.
 *
 * `passkey.ts` produces a *third peer* of the two wraps above, so it has to
 * seal under the same label — a wrap made with a different one would be a
 * lookalike that could not be swapped in, and the whole point is that all three
 * are interchangeable. The other three labels stay private because nothing
 * outside this file has any business wrapping a node or a private key.
 */
export const WRAP_AAD_MASTER = WRAP_AAD.master;

export class VaultLockedError extends Error {
  constructor(message = 'The vault is locked.') {
    super(message);
  }
}

export class WrongSecretError extends Error {
  constructor(
    readonly kind: 'password' | 'recovery',
    message: string
  ) {
    super(message);
  }
}

/** Open a vault with the account password. */
export async function unlockWithPassword(
  password: string,
  blobs: VaultBlobs
): Promise<UnlockedVault> {
  const kek = await deriveKek(password, fromBase64(blobs.kekSalt), blobs.kdfParams);
  return openVault(kek, blobs.masterKeyWrappedPassword, blobs, 'password');
}

/** Open a vault with the 24-word phrase. */
export async function unlockWithRecovery(
  entropy: Bytes,
  blobs: VaultBlobs
): Promise<UnlockedVault> {
  const rk = await deriveRecoveryKey(entropy);
  return openVault(rk, blobs.masterKeyWrappedRecovery, blobs, 'recovery');
}

async function openVault(
  wrappingKey: CryptoKey,
  wrapped: string,
  blobs: VaultBlobs,
  kind: 'password' | 'recovery'
): Promise<UnlockedVault> {
  let masterRaw: Bytes;
  try {
    masterRaw = await aesDecrypt(wrappingKey, wrapped, WRAP_AAD.master);
  } catch {
    // A failed GCM tag is indistinguishable from a wrong key, which is exactly
    // what this is in practice.
    throw new WrongSecretError(
      kind,
      kind === 'password'
        ? 'That password does not open this vault.'
        : 'That recovery phrase does not open this vault.'
    );
  }

  const masterKey = await importAesKey(masterRaw, false);
  masterRaw.fill(0);

  const privateKey = await unwrapPrivateKey(masterKey, blobs.privateKeyWrapped);
  const publicKey = await importPublicKey(blobs.publicKey);

  // The server serves `publicKey`, so verify it is the one this private key
  // belongs to rather than trusting the row. A substituted public key would
  // otherwise silently redirect everything shared *with* this user.
  const { fingerprint } = await exportPublicKey(publicKey);
  if (fingerprint !== blobs.publicKeyFingerprint) {
    throw new Error('This vault’s public key does not match its fingerprint.');
  }

  return { masterKey, privateKey, publicKey, publicKeyFingerprint: fingerprint };
}

/**
 * Open a vault from a master key that is already in hand.
 *
 * The "trust this device" path: the master key came back from IndexedDB, still
 * non-extractable, so there is no secret to derive and nothing to stretch. The
 * private key is still unwrapped from the server's blob on every restore rather
 * than being cached alongside — that way a key rotated server-side cannot be
 * shadowed by a stale local copy.
 */
export async function unlockWithMasterKey(
  masterKey: CryptoKey,
  blobs: VaultBlobs
): Promise<UnlockedVault> {
  const privateKey = await unwrapPrivateKey(masterKey, blobs.privateKeyWrapped);
  const publicKey = await importPublicKey(blobs.publicKey);

  const { fingerprint } = await exportPublicKey(publicKey);
  if (fingerprint !== blobs.publicKeyFingerprint) {
    throw new Error('This vault’s public key does not match its fingerprint.');
  }

  return { masterKey, privateKey, publicKey, publicKeyFingerprint: fingerprint };
}

/**
 * Re-wrap the master key under a new password.
 *
 * Used by "change password" and by recovery, which lands here after the phrase
 * has opened the vault. The master key itself never changes, so nothing in the
 * tree below it is re-encrypted — a password change is one row update, whatever
 * the size of the account.
 */
export async function rewrapForPassword(
  vault: UnlockedVault,
  newPassword: string,
  params: KdfParams = DEFAULT_KDF_PARAMS
): Promise<{ kekSalt: string; kdfParams: KdfParams; masterKeyWrappedPassword: string }> {
  const kekSalt = randomBytes(KEK_SALT_BYTES);
  const kek = await deriveKek(newPassword, kekSalt, params);
  const masterRaw = await exportMasterKey(vault);
  const wrapped = await aesEncrypt(kek, masterRaw, WRAP_AAD.master);
  masterRaw.fill(0);
  return { kekSalt: toBase64(kekSalt), kdfParams: params, masterKeyWrappedPassword: wrapped };
}

/** Re-wrap the master key under a freshly generated recovery phrase, retiring
 *  the old one — the previous wrap is overwritten, so the old words stop
 *  working the moment this is saved. */
export async function rewrapForRecovery(
  vault: UnlockedVault,
  recoveryEntropy: Bytes
): Promise<{ masterKeyWrappedRecovery: string }> {
  const rk = await deriveRecoveryKey(recoveryEntropy);
  const masterRaw = await exportMasterKey(vault);
  const wrapped = await aesEncrypt(rk, masterRaw, WRAP_AAD.master);
  masterRaw.fill(0);
  return { masterKeyWrappedRecovery: wrapped };
}

/**
 * The master key's bytes.
 *
 * Only rewrapping needs these, and `openVault` imports the master key
 * non-extractable — so this deliberately cannot read it back out. Instead the
 * two rewrap paths above are handed a vault that came from `createVault` or
 * from an unlock that kept the raw copy alive. Rather than weaken the import,
 * rewrapping re-derives from the wrapped copy the caller already holds.
 */
async function exportMasterKey(vault: UnlockedVault): Promise<Bytes> {
  if (!vault.masterKey.extractable) {
    throw new Error(
      'Master key is non-extractable; rewrap via unlockForRewrap() rather than an open vault.'
    );
  }
  return exportRaw(vault.masterKey);
}

/**
 * Unlock specifically in order to re-wrap.
 *
 * The everyday unlock path imports the master key non-extractable, which is
 * what makes an XSS unable to steal it. Changing a password genuinely needs the
 * bytes, so that one flow re-derives them here, under the caller's existing
 * secret, and the extractable copy lives only for the length of the rewrap.
 */
export async function unlockForRewrap(
  secret: { password: string } | { entropy: Bytes },
  blobs: VaultBlobs
): Promise<UnlockedVault> {
  const wrappingKey =
    'password' in secret
      ? await deriveKek(secret.password, fromBase64(blobs.kekSalt), blobs.kdfParams)
      : await deriveRecoveryKey(secret.entropy);

  const wrapped =
    'password' in secret ? blobs.masterKeyWrappedPassword : blobs.masterKeyWrappedRecovery;

  let masterRaw: Bytes;
  try {
    masterRaw = await aesDecrypt(wrappingKey, wrapped, WRAP_AAD.master);
  } catch {
    throw new WrongSecretError(
      'password' in secret ? 'password' : 'recovery',
      'That secret does not open this vault.'
    );
  }

  const masterKey = await importAesKey(masterRaw, true);
  masterRaw.fill(0);

  const privateKey = await unwrapPrivateKey(masterKey, blobs.privateKeyWrapped);
  const publicKey = await importPublicKey(blobs.publicKey);
  const { fingerprint } = await exportPublicKey(publicKey);

  return { masterKey, privateKey, publicKey, publicKeyFingerprint: fingerprint };
}

// ── the sharing key pair ────────────────────────────────────────────────────

const EC_PARAMS: EcKeyGenParams = { name: 'ECDH', namedCurve: 'P-256' };

/** ECDH P-256. Not X25519, which would be the better curve, because Safari
 *  still does not implement it in WebCrypto and the alternative is doing curve
 *  arithmetic over extractable JS values. */
export const generateUserKeyPair = (): Promise<CryptoKeyPair> =>
  crypto.subtle.generateKey(EC_PARAMS, true, ['deriveKey', 'deriveBits']) as Promise<CryptoKeyPair>;

export async function exportPublicKey(
  publicKey: CryptoKey
): Promise<{ spki: string; fingerprint: string }> {
  const raw = new Uint8Array(await crypto.subtle.exportKey('spki', publicKey));
  return { spki: toBase64(raw), fingerprint: toHex(await sha256(raw)) };
}

export const importPublicKey = (spki: string): Promise<CryptoKey> =>
  crypto.subtle.importKey('spki', fromBase64(spki), EC_PARAMS, true, []);

/**
 * The fingerprint of a public key we did not generate.
 *
 * Same derivation as `exportPublicKey` — SHA-256 of the SPKI bytes, hex — and
 * that is the point rather than a convenience. When a desktop app hands its key
 * to the browser in a URL fragment, the fingerprint is the idempotency key the
 * two sides look the row up by: the browser registers the device under it and
 * the app finds that row under it later. Two derivations that disagreed by a
 * byte would leave the app registering a second, unapproved device and waiting
 * for an envelope nobody was going to write.
 */
export const fingerprintOfSpki = async (spki: string): Promise<string> =>
  toHex(await sha256(fromBase64(spki)));

async function wrapPrivateKey(masterKey: CryptoKey, privateKey: CryptoKey): Promise<string> {
  const pkcs8 = new Uint8Array(await crypto.subtle.exportKey('pkcs8', privateKey));
  const wrapped = await aesEncrypt(masterKey, pkcs8, WRAP_AAD.private);
  pkcs8.fill(0);
  return wrapped;
}

async function unwrapPrivateKey(masterKey: CryptoKey, wrapped: string): Promise<CryptoKey> {
  const pkcs8 = await aesDecrypt(masterKey, wrapped, WRAP_AAD.private);
  const key = await crypto.subtle.importKey('pkcs8', pkcs8, EC_PARAMS, false, [
    'deriveKey',
    'deriveBits'
  ]);
  pkcs8.fill(0);
  return key;
}

/**
 * A fingerprint a human can read out loud, in groups of four hex characters.
 *
 * The server hands out public keys, so it could substitute its own and read
 * everything shared with that user afterwards. Nothing in the protocol prevents
 * that — comparing this string out of band is what detects it, which is why it
 * is surfaced in the UI rather than kept as an internal detail.
 */
export const readableFingerprint = (fingerprint: string): string =>
  (fingerprint.match(/.{1,4}/g) ?? []).slice(0, 8).join(' ').toUpperCase();

// ── node and content keys ───────────────────────────────────────────────────

export const generateNodeKey = generateAesKey;
export const generateContentKey = generateAesKey;

/** Wrap a node key under its parent's key — or, for the root, the master key. */
export async function wrapNodeKey(parentKey: CryptoKey, nodeKey: CryptoKey): Promise<string> {
  const raw = await exportRaw(nodeKey);
  const wrapped = await aesEncrypt(parentKey, raw, WRAP_AAD.node);
  raw.fill(0);
  return wrapped;
}

export async function unwrapNodeKey(parentKey: CryptoKey, wrapped: string): Promise<CryptoKey> {
  const raw = await aesDecrypt(parentKey, wrapped, WRAP_AAD.node);
  // Extractable: a node key gets re-wrapped whenever the node is moved or
  // shared, and it is only ever reachable by someone who already holds the
  // chain above it.
  const key = await importAesKey(raw, true);
  raw.fill(0);
  return key;
}

export async function wrapContentKey(nodeKey: CryptoKey, contentKey: CryptoKey): Promise<string> {
  const raw = await exportRaw(contentKey);
  const wrapped = await aesEncrypt(nodeKey, raw, WRAP_AAD.content);
  raw.fill(0);
  return wrapped;
}

export async function unwrapContentKey(nodeKey: CryptoKey, wrapped: string): Promise<CryptoKey> {
  const raw = await aesDecrypt(nodeKey, wrapped, WRAP_AAD.content);
  const key = await importAesKey(raw, false);
  raw.fill(0);
  return key;
}
